import type * as React from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

/**
 * Says why a control is disabled, on hover and on keyboard focus. A disabled
 * `<button>` swallows pointer events (`Button` is `disabled:pointer-events-none`)
 * and can't take focus, so neither a `title` nor a tooltip on the button itself
 * ever shows. The control is wrapped in a focusable span that carries the
 * tooltip instead.
 *
 * Without a `reason` the control renders on its own, so callers can wrap
 * unconditionally and pass the reason only while the control is disabled.
 */
function DisabledReasonTooltip({
  reason,
  children,
}: {
  /** Why the control is disabled. Null, undefined or empty renders `children` alone. */
  reason: string | null | undefined;
  /** The control — disabled whenever a reason is given. */
  children: React.ReactNode;
}) {
  if (reason == null || reason.length === 0) return <>{children}</>;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} data-slot="disabled-reason-trigger" className="inline-flex">
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent>{reason}</TooltipContent>
    </Tooltip>
  );
}

export { DisabledReasonTooltip };
